import {
  useCallback,
  useEffect,
  useState,
  type CSSProperties,
  type DragEvent as ReactDragEvent,
  type ReactElement,
} from 'react';
import { FDA_FORM_TYPES } from '@shared/fdaFormTypes';
import { sectionKey } from '@/lib/sectionKey';
import { describeApiError } from '@/lib/apiErrorView';
import type { Document } from '@/api/v2Client';
import { v2, parseApiErrorBody } from '@/api/v2Client';
import { ConfirmModal } from '@/components/shared/ConfirmModal';
import { SignatureBadge } from '@/components/documents/SignatureBadge';
import { DocumentBadge } from './DocumentBadge';
import { parseLifecycleOperation } from './lifecycle';
import { swapAdjacent } from './reorderDocs';
import { LockStateIcon } from './LockStateIcon';
import { useSubmissionDraft } from '@/hooks/useSubmissionWorkspace';

/**
 * Wave E.1 — Middle-pane document list. Wave E.2 added confirmed pills.
 * Wave E.3 adds:
 *   - Drag-to-replace: drop a file from the OS onto an existing row,
 *     uploads the file then prompts ConfirmModal to commit the replace.
 *   - Lifecycle badges next to each doc title (NEW / REPLACE / APPEND / DELETE).
 *   - Strike-through styling on DELETE rows.
 *
 * Five rendered states (unchanged from Wave E.1):
 *   - no section selected → "Pick a section in the tree."
 *   - loading             → spinner placeholder
 *   - error               → red error block
 *   - empty               → "No documents at this section."
 *   - populated           → list of doc rows, click selects one
 */

// Phase hc-dual-format-note (TODO-063) ─────────────────────────────────────
// HC requires both PDF and DOCX for Product Monograph sections.
// Mirror of V1's ReviewScreen HC_DUAL_FORMAT_SECTIONS check.

const HC_DUAL_FORMAT_SECTIONS = ['m1/1.3', 'm1/1.3.1', 'm1/1.3.2'] as const;

function requiresDualFormat(doc: Document, region: string): boolean {
  return (
    region === 'Health Canada' &&
    HC_DUAL_FORMAT_SECTIONS.some((s) => doc.CtdSectionCode?.startsWith(s))
  );
}

/** True when the section (all docs sharing the same CtdSectionCode) has at least one PDF file. */
function sectionHasPdf(doc: Document, allDocs: readonly Document[]): boolean {
  return allDocs.some(
    (d) =>
      d.CtdSectionCode === doc.CtdSectionCode &&
      d.FileName?.toLowerCase().endsWith('.pdf'),
  );
}

/** True when the section has at least one DOCX/DOC file. */
function sectionHasDocx(doc: Document, allDocs: readonly Document[]): boolean {
  return allDocs.some(
    (d) =>
      d.CtdSectionCode === doc.CtdSectionCode &&
      (d.FileName?.toLowerCase().endsWith('.docx') ||
        d.FileName?.toLowerCase().endsWith('.doc')),
  );
}

// ────────────────────────────────────────────────────────────────────────────

export interface DocumentListProps {
  readonly sectionCode: string | null;
  /**
   * phase-ui-compiler-tree-documents-pane — when true, skip the list's own
   * header + section subtitle (the grouped all-documents view renders its own
   * slim group headers) and render rows even with sectionCode=null (the
   * Unplaced group). Default false: existing single-section behaviour.
   */
  readonly hideHeader?: boolean;
  readonly documents: readonly Document[];
  readonly selectedDocumentId: string | null;
  /**
   * Phase hc-dual-format-note (TODO-063) — submission region string
   * (e.g. 'Health Canada', 'FDA'). When provided, HC dual-format notes
   * are rendered for Product Monograph sections.
   */
  readonly region?: string;
  /**
   * Wave E.3 — parent submission id; required for the drag-to-replace
   * flow (the new upload needs a SubmissionId on creation). When null,
   * drops are accepted but produce an error toast. Optional for callers
   * that don't enable drag-replace.
   */
  readonly submissionId?: string | null;
  /**
   * Wave E.3 — when false (default), drag-to-replace is disabled.
   * Controls the front-end UX affordance; the backend enforces the
   * same role gate at /api/v1/documents/:id/replace.
   */
  readonly canEdit?: boolean;
  readonly sequenceLocked?: boolean;
  readonly onSelect: (documentId: string) => void;
  readonly loading?: boolean;
  readonly error?: string | null;
  /**
   * Wave E.3 — called after a successful replace OR after a successful
   * file upload that was cancelled at the confirm step (so the parent
   * can refresh the doc list either way: the cancelled upload lingers
   * in the same section per the prompt's "leave it" default).
   */
  readonly onListChanged?: () => void;
  /** Optional error reporter; parent typically forwards to a toast. */
  readonly onError?: (message: string) => void;
  /**
   * Phase sibling-selector-toolbar (TODO-058 Part B) — documents drawn
   * from an overlaid sibling (prior) sequence. Rendered AFTER the
   * current sequence's rows in a muted style with a "from seq NNNN"
   * badge. Non-interactive (no select, no drag/drop, no lock toggle).
   * Empty/undefined → no sibling section is rendered.
   */
  readonly siblingDocuments?: readonly Document[];
  /**
   * 4-digit sibling sequence label shown in the badge ("from seq 0001").
   * Required when `siblingDocuments` is non-empty.
   */
  readonly siblingSequenceNumber?: string | null;
  /**
   * Phase compiler-batch-operations — called after a successful batch
   * confirm so the parent can push an undo entry. Receives the confirmed
   * document IDs.
   */
  readonly onBatchConfirmed?: (ids: string[]) => void;
  /**
   * Phase compiler-batch-operations — called after a successful batch
   * lock/unlock so the parent can push an undo entry.
   */
  readonly onBatchLocked?: (ids: string[], locked: boolean) => void;
  /**
   * Phase undo-stack-remaining-mutations — called after a successful
   * individual lock toggle so the parent can push an undo entry.
   * `wasLocked` is the state BEFORE the toggle (i.e. the inverse to restore).
   */
  readonly onLockToggled?: (docId: string, docName: string, wasLocked: boolean) => void;
  /**
   * Phase undo-stack-remaining-mutations — called after a successful
   * reorder with the original document order (before the reorder) so
   * the parent can push an undo entry that restores it.
   */
  readonly onReordered?: (originalOrderIds: string[]) => void;
}

const titleStyle: CSSProperties = {
  margin: '0 0 4px 0',
  fontSize: 14,
  fontWeight: 600,
  color: 'var(--text)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
};

const subtitleStyle: CSSProperties = {
  margin: '0 0 12px 0',
  fontSize: 12,
  color: 'var(--text-muted)',
  fontFamily: 'monospace',
};

const placeholderStyle: CSSProperties = {
  padding: '20px 12px',
  fontSize: 13,
  color: 'var(--text-muted)',
  fontStyle: 'italic',
  textAlign: 'center',
};

const errorStyle: CSSProperties = {
  padding: '20px 12px',
  fontSize: 13,
  color: 'var(--danger, #b91c1c)',
};

const rowBaseStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  padding: '8px 10px',
  borderRadius: 6,
  cursor: 'pointer',
  border: '1px solid transparent',
  position: 'relative',
};

const rowSelectedStyle: CSSProperties = {
  background: 'var(--accent-bg, rgba(99,102,241,0.10))',
  borderColor: 'var(--accent, #4f46e5)',
};

const rowDragOverStyle: CSSProperties = {
  background: 'rgba(59,130,246,0.10)',
  borderColor: 'rgba(59,130,246,0.55)',
  borderStyle: 'dashed',
};

const fileNameStyle: CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--text)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  display: 'flex',
  alignItems: 'center',
  gap: 6,
};

const metaStyle: CSSProperties = {
  fontSize: 11,
  color: 'var(--text-muted)',
  fontFamily: 'monospace',
};

const confirmedBadge: CSSProperties = {
  display: 'inline-block',
  marginLeft: 6,
  fontSize: 10,
  padding: '1px 6px',
  borderRadius: 999,
  background: 'var(--ok-bg, rgba(34,197,94,0.18))',
  color: 'var(--ok, #15803d)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

function formatBytes(bytes: number | null | undefined): string {
  if (typeof bytes !== 'number' || !Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ----- Phase compiler-spl-review-badge (TODO-084, TODO-085) ----- //

/**
 * SPL (Structured Product Labelling) sections that demand an XML+XSL
 * pairing per FDA SPL guidance. Mirrors V1 ReviewScreen's hint logic.
 * Includes the common SPL placements; if more emerge we can widen the
 * set without changing the helper signature.
 */
const SPL_SECTION_PREFIXES = ['m1/1.14.1', 'm1/1.3.1', 'm1/1.3.2'] as const;

function isSplXmlDoc(doc: Document): boolean {
  const name = (doc.FileName ?? '').toLowerCase();
  if (!name.endsWith('.xml')) return false;
  const sec = (doc.CtdSectionCode ?? '') as string;
  return SPL_SECTION_PREFIXES.some((p) => sec.startsWith(p));
}

function sectionHasXsl(doc: Document, allDocs: readonly Document[]): boolean {
  return allDocs.some(
    (d) =>
      d.CtdSectionCode === doc.CtdSectionCode &&
      (d.FileName ?? '').toLowerCase().endsWith('.xsl'),
  );
}

type ReviewSource = 'ai' | 'auto-confirmed' | 'human' | 'reclassified';

/**
 * TODO-085 — derive the review-source badge for a document row.
 *   - 'reclassified' wins when the human override (ClassificationApprovedValue
 *     or CtdSectionCode) differs from the classifier's suggestion
 *     (Classification field).
 *   - else 'human' when ConfirmedAt is set and AutoConfirmed is not true.
 *   - else 'auto-confirmed' when ConfirmedAt is set AND AutoConfirmed=true.
 *   - else 'ai' (unconfirmed AI suggestion only).
 */
function reviewSource(doc: Document): ReviewSource {
  const ai = (doc.Classification ?? null) as string | null;
  const approved =
    ((doc['ClassificationApprovedValue'] as string | null | undefined) ?? null) ??
    ((doc.CtdSectionCode ?? null) as string | null);
  const confirmed = typeof doc.ConfirmedAt === 'string' && doc.ConfirmedAt.length > 0;
  if (ai && approved && ai !== approved) return 'reclassified';
  if (confirmed && doc.AutoConfirmed === true) return 'auto-confirmed';
  if (confirmed) return 'human';
  return 'ai';
}

const sourceBadgeBase: CSSProperties = {
  display: 'inline-block',
  marginLeft: 6,
  fontSize: 10,
  padding: '1px 6px',
  borderRadius: 4,
  border: '1px solid var(--border)',
  background: 'var(--surface2)',
  color: 'var(--text-muted)',
  letterSpacing: '0.04em',
  whiteSpace: 'nowrap',
};

function sourceBadgeLabel(s: ReviewSource): string {
  switch (s) {
    case 'ai': return 'AI';
    case 'auto-confirmed': return 'AI ✓';
    case 'human': return '✓';
    case 'reclassified': return '↺ Reclassified';
  }
}

interface PendingReplace {
  readonly targetDoc: Document;
  readonly newDocumentId: string;
  readonly newFileName: string;
}

export function DocumentList({
  sectionCode,
  hideHeader = false,
  documents,
  selectedDocumentId,
  submissionId = null,
  canEdit = false,
  sequenceLocked = false,
  region = '',
  onSelect,
  loading = false,
  error = null,
  onListChanged,
  onError,
  siblingDocuments,
  siblingSequenceNumber = null,
  onBatchConfirmed,
  onBatchLocked,
  onLockToggled,
  onReordered,
}: DocumentListProps): ReactElement {
  const [dragOverDocId, setDragOverDocId] = useState<string | null>(null);
  // Phase UI-13 — inline rename state. `renameId` is the doc currently
  // being renamed; null means no rename in progress. `renameValue`
  // tracks the input value; the per-row Save button PATCHes FileName ONLY
  // (COV019) — ModifiedFile is the eCTD lifecycle reference to a PRIOR
  // sequence's file and is never part of a rename payload.
  const [renameId, setRenameId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameRevision, setRenameRevision] = useState<Document['RowVersion']>();
  const [renameBusy, setRenameBusy] = useState(false);
  const discardRename = useCallback(() => { setRenameId(null); setRenameValue(''); }, []);
  const renamedDocument = documents.find((doc) => doc.Id === renameId);
  useSubmissionDraft(Boolean(renamedDocument && renameValue !== renamedDocument.FileName) || renameBusy, discardRename, renameBusy);
  // Tracks docs currently being uploaded-into (so the row shows a spinner
  // / disabled state while the file binary is in flight).
  const [uploadingDocId, setUploadingDocId] = useState<string | null>(null);
  // The pending-confirm state — non-null means the ConfirmModal is open.
  const [pending, setPending] = useState<PendingReplace | null>(null);
  // True while the replace POST is in flight.
  const [replacing, setReplacing] = useState(false);
  // Phase 5.X-medium-batch-web — toggle between canonical eCTD filename
  // and the original uploaded filename.
  const [showOriginal, setShowOriginal] = useState(false);
  const [formTypes, setFormTypes] = useState<Record<string, string | null>>({});
  useEffect(() => { setFormTypes({}); }, [documents]);
  const [formTypeBusy, setFormTypeBusy] = useState<string | null>(null);
  const [formTypeError, setFormTypeError] = useState<{ id: string; message: string } | null>(null);

  // phase-document-reorder-within-section — local optimistic ordering.
  // null means "use the server order from props.documents directly".
  // Set to a reordered snapshot on drag-drop / move-up / move-down;
  // cleared after the API call resolves (parent refreshes on onListChanged).
  const [localDocs, setLocalDocs] = useState<readonly Document[] | null>(null);
  // When the parent refreshes the documents prop, clear any stale local order.
  useEffect(() => {
    setLocalDocs(null);
  }, [documents]);
  // Effective display list — local ordering takes precedence.
  const displayDocs = localDocs ?? documents;

  // Phase compiler-document-lock (TODO-079) — optimistic per-document lock
  // override. Maps doc.Id -> in-flight desired IsLocked value. Cleared per
  // doc after the API resolves (or reverts on error).
  const [lockOverrides, setLockOverrides] = useState<Record<string, boolean>>({});

  // Phase compiler-batch-operations — multi-select state.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [batchBusy, setBatchBusy] = useState(false);
  // Clear selection when the documents list changes (section change / refresh).
  useEffect(() => {
    setSelectedIds(new Set());
  }, [documents]);

  const reportError = useCallback(
    (msg: string) => {
      if (onError) onError(msg);
      else console.warn('[DocumentList] error:', msg);
    },
    [onError],
  );

  // phase-document-reorder-within-section — move sourceId to just before
  // targetId in the current display order.
  const reorderDocs = useCallback(
    (sourceId: string, targetId: string): readonly Document[] => {
      const current = [...displayDocs];
      const srcIdx = current.findIndex((d) => d.Id === sourceId);
      const tgtIdx = current.findIndex((d) => d.Id === targetId);
      if (srcIdx === -1 || tgtIdx === -1 || srcIdx === tgtIdx) return current;
      const [moved] = current.splice(srcIdx, 1);
      const insertAt = srcIdx < tgtIdx ? tgtIdx : tgtIdx;
      current.splice(insertAt, 0, moved);
      return current;
    },
    [displayDocs],
  );

  // phase-compiler-chrome-batch item 4 — the swap itself is the SHARED
  // `swapAdjacent` (reorderDocs.ts), the same evaluator the CTD tree's inline
  // arrows call, so the two placement surfaces cannot drift.
  const swapDocs = useCallback(
    (docId: string, direction: 'up' | 'down'): readonly Document[] =>
      swapAdjacent(displayDocs, docId, direction),
    [displayDocs],
  );

  const commitReorder = useCallback(
    async (newOrder: readonly Document[]): Promise<void> => {
      if (!submissionId) {
        reportError('Cannot reorder: parent submission id is missing.');
        return;
      }
      // Phase undo-stack-remaining-mutations — capture the original order
      // (displayDocs at call time) so the parent can restore it on undo.
      const originalOrderIds = displayDocs.map((d) => d.Id);
      setLocalDocs(newOrder);
      try {
        await v2.documentReorder.reorder(
          submissionId,
          newOrder.map((d) => d.Id),
        );
        setLocalDocs(null);
        if (onListChanged) onListChanged();
        if (onReordered) onReordered(originalOrderIds);
      } catch (err) {
        setLocalDocs(null);
        const body = parseApiErrorBody(err);
        reportError(
          body?.message ?? body?.error ?? (err instanceof Error ? err.message : String(err)),
        );
      }
    },
    [submissionId, onListChanged, onReordered, reportError, displayDocs],
  );

  // Phase compiler-document-lock (TODO-079) — toggle lock with optimistic UI.
  const toggleLock = useCallback(
    async (doc: Document): Promise<void> => {
      const current = lockOverrides[doc.Id] ?? (doc.IsLocked === true);
      const next = !current;
      setLockOverrides((m) => ({ ...m, [doc.Id]: next }));
      try {
        await v2.documents.setLock(doc.Id, next);
        if (onListChanged) onListChanged();
        // Phase undo-stack-remaining-mutations — notify parent with pre-toggle state.
        if (onLockToggled) onLockToggled(doc.Id, doc.FileName, current);
        // Drop the override once parent refresh propagates the server state.
        setLockOverrides((m) => {
          const rest = { ...m };
          delete rest[doc.Id];
          return rest;
        });
      } catch (err) {
        // Revert on error.
        setLockOverrides((m) => {
          const rest = { ...m };
          delete rest[doc.Id];
          return rest;
        });
        const body = parseApiErrorBody(err);
        reportError(
          body?.message ?? body?.error ?? (err instanceof Error ? err.message : String(err)),
        );
      }
    },
    [lockOverrides, onListChanged, onLockToggled, reportError],
  );

  // Phase compiler-batch-operations — batch confirm selected documents.
  const handleBatchConfirm = useCallback(async (): Promise<void> => {
    if (!submissionId || selectedIds.size === 0) return;
    setBatchBusy(true);
    const ids = [...selectedIds];
    try {
      await v2.documents.bulkConfirm(submissionId, { documentIds: ids });
      setSelectedIds(new Set());
      if (onListChanged) onListChanged();
      if (onBatchConfirmed) onBatchConfirmed(ids);
    } catch (err) {
      const body = parseApiErrorBody(err);
      reportError(body?.message ?? body?.error ?? (err instanceof Error ? err.message : String(err)));
    } finally {
      setBatchBusy(false);
    }
  }, [submissionId, selectedIds, onListChanged, onBatchConfirmed, reportError]);

  // Phase compiler-batch-operations — batch lock/unlock selected documents.
  const handleBatchLock = useCallback(async (locked: boolean): Promise<void> => {
    if (!submissionId || selectedIds.size === 0) return;
    setBatchBusy(true);
    const ids = [...selectedIds];
    try {
      await v2.documents.batchSetLock(submissionId, ids, locked);
      setSelectedIds(new Set());
      if (onListChanged) onListChanged();
      if (onBatchLocked) onBatchLocked(ids, locked);
    } catch (err) {
      const body = parseApiErrorBody(err);
      reportError(body?.message ?? body?.error ?? (err instanceof Error ? err.message : String(err)));
    } finally {
      setBatchBusy(false);
    }
  }, [submissionId, selectedIds, onListChanged, onBatchLocked, reportError]);

  // Handle a file dropped from the OS onto an existing doc row.
  // Sequence: create new Document row → upload file binary → open
  // ConfirmModal. On confirm: call replace endpoint. On cancel: leave
  // the upload in place per design (operator can re-classify/delete).
  const handleFileDrop = useCallback(
    async (targetDoc: Document, file: File): Promise<void> => {
      if (!canEdit) {
        reportError('Replacing documents requires Author or higher role.');
        return;
      }
      if (!submissionId) {
        reportError('Cannot replace: parent submission id is missing.');
        return;
      }
      setUploadingDocId(targetDoc.Id);
      // T-17 (T17-D5) — `create` records a PENDING upload that only becomes a
      // document when the upload below succeeds. If the upload fails the
      // pending record is dropped (best-effort: the server also purges stale
      // pending rows, so a failed cleanup call is harmless).
      let pendingId: string | null = null;
      try {
        // 1. Create a new Document row in the same submission.
        const created = await v2.documents.create({
          SubmissionId: submissionId,
          FileName: file.name,
        });
        pendingId = created.Id;
        // 2. Upload the binary onto that row.
        await v2.documents.uploadFile(created.Id, file);
        pendingId = null;
        // 3. Open the confirm dialog. The actual replace POST fires on confirm.
        setPending({
          targetDoc,
          newDocumentId: created.Id,
          newFileName: file.name,
        });
      } catch (err) {
        if (pendingId) {
          void v2.documents.abandonPendingUpload(pendingId).catch(() => {
            /* best-effort cleanup; never replaces the original error */
          });
        }
        const body = parseApiErrorBody(err);
        reportError(
          body?.message ?? body?.error ?? (err instanceof Error ? err.message : String(err)),
        );
      } finally {
        setUploadingDocId(null);
      }
    },
    [canEdit, submissionId, reportError],
  );

  const onRowDragOver = useCallback(
    (docId: string) =>
      (e: ReactDragEvent<HTMLDivElement>): void => {
        if (!canEdit) return;
        // Allow both OS file drags and internal row-to-row reorders.
        const isFileDrag = e.dataTransfer.types.includes('Files');
        const isInternalDrag = e.dataTransfer.types.includes('application/x-document-id');
        if (!isFileDrag && !isInternalDrag) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = isFileDrag ? 'copy' : 'move';
        if (dragOverDocId !== docId) setDragOverDocId(docId);
      },
    [canEdit, dragOverDocId],
  );

  const onRowDragLeave = useCallback(
    (docId: string) =>
      (e: ReactDragEvent<HTMLDivElement>): void => {
        // Avoid flicker when entering child elements — only clear if the
        // related target is OUTSIDE the row.
        const next = e.relatedTarget as globalThis.Node | null;
        if (next && e.currentTarget.contains(next)) return;
        if (dragOverDocId === docId) setDragOverDocId(null);
      },
    [dragOverDocId],
  );

  const onRowDrop = useCallback(
    (doc: Document) =>
      (e: ReactDragEvent<HTMLDivElement>): void => {
        if (!canEdit) return;
        e.preventDefault();
        setDragOverDocId(null);

        // Internal document reorder — drag handle sets this data type.
        if (e.dataTransfer.types.includes('application/x-document-id')) {
          const sourceId = e.dataTransfer.getData('application/x-document-id');
          if (sourceId && sourceId !== doc.Id) {
            void commitReorder(reorderDocs(sourceId, doc.Id));
          }
          return;
        }

        // OS file drop → drag-to-replace flow.
        if (!e.dataTransfer.types.includes('Files')) return;
        const file = e.dataTransfer.files?.[0];
        if (!file) {
          reportError('No file detected in drop.');
          return;
        }
        void handleFileDrop(doc, file);
      },
    [canEdit, handleFileDrop, reportError, commitReorder, reorderDocs],
  );

  const handleConfirmReplace = useCallback(async (): Promise<void> => {
    if (!pending) return;
    setReplacing(true);
    try {
      await v2.documents.replace(pending.targetDoc.Id, {
        newDocumentId: pending.newDocumentId,
      });
      setPending(null);
      if (onListChanged) onListChanged();
    } catch (err) {
      const body = parseApiErrorBody(err);
      reportError(
        body?.message ?? body?.error ?? (err instanceof Error ? err.message : String(err)),
      );
    } finally {
      setReplacing(false);
    }
  }, [pending, onListChanged, reportError]);

  const handleCancelReplace = useCallback((): void => {
    if (replacing) return;
    // Per design spec: the uploaded doc remains in place. Operator can
    // re-classify or delete via the existing UI. We still notify the
    // parent so the list reloads (the new doc is now in the section).
    setPending(null);
    if (onListChanged) onListChanged();
  }, [replacing, onListChanged]);

  // Phase UI-13 — inline rename handlers.
  const startRename = useCallback((doc: Document) => {
    setRenameId(doc.Id);
    setRenameRevision(doc.RowVersion);
    setRenameValue(doc.FileName);
  }, []);
  const saveRename = useCallback(
    async (docId: string): Promise<void> => {
      if (!renameValue.trim()) return;
      setRenameBusy(true);
      try {
        await v2.documents.update(docId, { FileName: renameValue.trim() }, renameRevision);
        setRenameId(null);
        setRenameValue('');
        if (onListChanged) onListChanged();
      } catch (err) {
        const body = parseApiErrorBody(err);
        reportError(
          body?.message ?? body?.error ?? (err instanceof Error ? err.message : String(err)),
        );
      } finally {
        setRenameBusy(false);
      }
    },
    [renameValue, renameRevision, onListChanged, reportError],
  );
  const autoRename = useCallback(
    async (docId: string): Promise<void> => {
      setRenameBusy(true);
      try {
        const revision = documents.find((doc) => doc.Id === docId)?.RowVersion;
        const res = await v2.documents.autoRename(docId);
        if (res.suggestedName) {
          await v2.documents.update(docId, { FileName: res.suggestedName }, revision);
          if (onListChanged) onListChanged();
        }
      } catch (err) {
        const body = parseApiErrorBody(err);
        reportError(
          body?.message ?? body?.error ?? (err instanceof Error ? err.message : String(err)),
        );
      } finally {
        setRenameBusy(false);
      }
    },
    [documents, onListChanged, reportError],
  );

  return (
    <div data-testid="document-list">
      {!hideHeader && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
            <h2 style={{ ...titleStyle, margin: 0 }}>Documents</h2>
            <button
              data-testid="show-original-names-toggle"
              title={showOriginal ? 'Show canonical eCTD filename' : 'Show original uploaded filename'}
              onClick={() => setShowOriginal((v) => !v)}
              style={{
                background: showOriginal ? 'var(--accent-bg, rgba(99,102,241,0.15))' : 'none',
                border: '1px solid var(--border, #e2e8f0)',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: 11,
                padding: '2px 7px',
                color: showOriginal ? 'var(--accent, #4f46e5)' : 'var(--text-muted)',
              }}
            >
              ⇄ {showOriginal ? 'Original' : 'eCTD'}
            </button>
          </div>
          {sectionCode ? (
            <div style={subtitleStyle}>Section: {sectionCode}</div>
          ) : (
            <div style={subtitleStyle}>(no section selected)</div>
          )}
        </>
      )}

      {!sectionCode && !hideHeader ? (
        <div data-testid="document-list-no-section" style={placeholderStyle}>
          Pick a section in the tree to see its documents.
        </div>
      ) : loading ? (
        <div data-testid="document-list-loading" style={placeholderStyle}>
          Loading documents…
        </div>
      ) : error ? (
        <div data-testid="document-list-error" style={errorStyle} role="alert">
          {error}
        </div>
      ) : displayDocs.length === 0 ? (
        <div data-testid="document-list-empty" style={placeholderStyle}>
          No documents at section {sectionCode}.
        </div>
      ) : (
        <div>
          {/* Phase compiler-batch-operations — select-all + batch toolbar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            <input
              type="checkbox"
              data-testid="batch-select-all"
              aria-label="Select all documents"
              checked={displayDocs.length > 0 && selectedIds.size === displayDocs.length}
              ref={(el) => {
                if (el) el.indeterminate = selectedIds.size > 0 && selectedIds.size < displayDocs.length;
              }}
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedIds(new Set(displayDocs.map((d) => d.Id)));
                } else {
                  setSelectedIds(new Set());
                }
              }}
              style={{ cursor: 'pointer' }}
            />
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              {selectedIds.size > 0 ? `${selectedIds.size} selected` : 'Select all'}
            </span>
          </div>
          {/* Batch action toolbar — slides in when selection > 0 */}
          {selectedIds.size > 0 && (
            <div
              data-testid="batch-toolbar"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 8px',
                marginBottom: 6,
                background: 'var(--accent-bg, rgba(99,102,241,0.10))',
                borderRadius: 6,
                border: '1px solid var(--accent, #4f46e5)',
                fontSize: 12,
              }}
            >
              <span style={{ fontWeight: 600, color: 'var(--accent, #4f46e5)', marginRight: 4 }}>
                {selectedIds.size} document{selectedIds.size !== 1 ? 's' : ''} selected
              </span>
              {canEdit && (
                <>
                  <button
                    type="button"
                    data-testid="batch-confirm-btn"
                    disabled={batchBusy}
                    onClick={() => { void handleBatchConfirm(); }}
                    style={{
                      fontSize: 11,
                      padding: '2px 8px',
                      border: '1px solid var(--ok, #15803d)',
                      borderRadius: 3,
                      background: 'var(--ok-bg, rgba(34,197,94,0.12))',
                      color: 'var(--ok, #15803d)',
                      cursor: batchBusy ? 'not-allowed' : 'pointer',
                      opacity: batchBusy ? 0.6 : 1,
                    }}
                  >
                    Confirm all
                  </button>
                  <button
                    type="button"
                    data-testid="batch-lock-btn"
                    disabled={batchBusy}
                    onClick={() => { void handleBatchLock(true); }}
                    style={{
                      fontSize: 11,
                      padding: '2px 8px',
                      border: '1px solid var(--border)',
                      borderRadius: 3,
                      background: 'var(--surface)',
                      cursor: batchBusy ? 'not-allowed' : 'pointer',
                      opacity: batchBusy ? 0.6 : 1,
                    }}
                  >
                    Lock all
                  </button>
                  <button
                    type="button"
                    data-testid="batch-unlock-btn"
                    disabled={batchBusy}
                    onClick={() => { void handleBatchLock(false); }}
                    style={{
                      fontSize: 11,
                      padding: '2px 8px',
                      border: '1px solid var(--border)',
                      borderRadius: 3,
                      background: 'var(--surface)',
                      cursor: batchBusy ? 'not-allowed' : 'pointer',
                      opacity: batchBusy ? 0.6 : 1,
                    }}
                  >
                    Unlock all
                  </button>
                </>
              )}
              <button
                type="button"
                data-testid="batch-clear-btn"
                disabled={batchBusy}
                onClick={() => setSelectedIds(new Set())}
                style={{
                  fontSize: 11,
                  padding: '2px 8px',
                  border: '1px solid var(--border)',
                  borderRadius: 3,
                  background: 'transparent',
                  cursor: batchBusy ? 'not-allowed' : 'pointer',
                  marginLeft: 'auto',
                  color: 'var(--text-muted)',
                }}
              >
                Clear selection
              </button>
            </div>
          )}
          <div role="list" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {displayDocs.map((doc, docIdx) => {
            const isSelected = doc.Id === selectedDocumentId;
            const isChecked = selectedIds.has(doc.Id);
            const confirmed = typeof doc.ConfirmedAt === 'string' && doc.ConfirmedAt.length > 0;
            const isDragOver = dragOverDocId === doc.Id;
            const isUploading = uploadingDocId === doc.Id;
            // Phase compiler-document-lock (TODO-079) — optimistic lock override
            // takes precedence over the server value while the API call is in flight.
            const isLocked =
              doc.Id in lockOverrides ? lockOverrides[doc.Id]! : doc.IsLocked === true;
            // Parse without defaulting so an unrecognised value renders an
            // honest "Unknown" badge (and no strike) rather than a fabricated
            // NEW. The raw value is handed to the badge; the parsed op only
            // drives strikethrough.
            const operation = parseLifecycleOperation(
              doc.LifecycleOperation as string | null | undefined,
            );
            const strikethrough = operation === 'delete';
            return (
              <div
                key={doc.Id}
                role="listitem"
                data-testid={`document-list-row-${doc.Id}`}
                data-locked={isLocked ? 'true' : 'false'}
                tabIndex={0}
                onClick={() => onSelect(doc.Id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelect(doc.Id);
                  }
                }}
                onDragOver={onRowDragOver(doc.Id)}
                onDragLeave={onRowDragLeave(doc.Id)}
                onDrop={onRowDrop(doc)}
                style={{
                  ...rowBaseStyle,
                  ...(isSelected ? rowSelectedStyle : null),
                  ...(isDragOver ? rowDragOverStyle : null),
                  ...(isUploading ? { opacity: 0.65, pointerEvents: 'none' } : null),
                  ...(isLocked
                    ? { background: 'var(--surface-muted, rgba(148,163,184,0.10))' }
                    : null),
                }}
              >
                <div style={fileNameStyle}>
                  {/* Phase compiler-batch-operations — per-row checkbox */}
                  <input
                    type="checkbox"
                    data-testid={`batch-checkbox-${doc.Id}`}
                    aria-label={`Select ${doc.FileName ?? doc.Id}`}
                    checked={isChecked}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => {
                      setSelectedIds((prev) => {
                        const next = new Set(prev);
                        if (e.target.checked) next.add(doc.Id);
                        else next.delete(doc.Id);
                        return next;
                      });
                    }}
                    style={{ cursor: 'pointer', flexShrink: 0 }}
                  />
                  <DocumentBadge operation={doc.LifecycleOperation as string | null | undefined} />
                  <span
                    data-testid={`document-list-filename-${doc.Id}`}
                    style={strikethrough ? { textDecoration: 'line-through' } : undefined}
                  >
                    {showOriginal ? (doc.OriginalFileName ?? doc.FileName) : doc.FileName}
                  </span>
                  {confirmed ? <span style={confirmedBadge}>confirmed</span> : null}
                  {/* Phase compiler-spl-review-badge (TODO-085) — review-source badge. */}
                  {(() => {
                    const src = reviewSource(doc);
                    return (
                      <span
                        data-testid={`review-source-badge-${doc.Id}`}
                        data-source={src}
                        title={
                          src === 'ai'
                            ? 'AI suggestion — not yet confirmed'
                            : src === 'auto-confirmed'
                              ? 'Auto-confirmed by high-confidence gate'
                              : src === 'reclassified'
                                ? 'Reclassified by a human reviewer (differs from AI suggestion)'
                                : 'Manually confirmed by a human reviewer'
                        }
                        style={sourceBadgeBase}
                      >
                        {sourceBadgeLabel(src)}
                      </span>
                    );
                  })()}
                  {/* Phase 5.X-document-handling-enhancements (Feature 1) —
                      signature badge. Renders nothing when status is null/none,
                      so unsigned docs are unchanged visually. */}
                  <SignatureBadge
                    status={doc.SignatureStatus as 'digital' | 'wet' | 'none' | null | undefined}
                  />
                  {isUploading ? (
                    <span
                      data-testid={`document-list-uploading-${doc.Id}`}
                      style={{ ...metaStyle, marginLeft: 6, color: 'var(--accent, #4f46e5)' }}
                    >
                      uploading…
                    </span>
                  ) : null}
                  {/* Phase compiler-document-lock (TODO-079) — toggle button.
                      Renders for everyone (read-only viewers see the state);
                      click is no-op when !canEdit. The closed-lock glyph is
                      always visible when locked; the open-lock is muted and
                      only fully opaque on hover (CSS `:hover` not used here,
                      we simply render it dimmer when unlocked). */}
                  <button
                    type="button"
                    aria-pressed={isLocked}
                    aria-label={isLocked ? 'Unlock document' : 'Lock document'}
                    title={isLocked ? 'Locked — click to unlock' : 'Click to lock'}
                    data-testid={`lock-toggle-${doc.Id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!canEdit) return;
                      void toggleLock(doc);
                    }}
                    disabled={!canEdit}
                    style={{
                      marginLeft: 'auto',
                      display: 'inline-flex',
                      alignItems: 'center',
                      background: 'transparent',
                      border: 'none',
                      cursor: canEdit ? 'pointer' : 'default',
                      padding: '0 4px',
                      lineHeight: 1,
                      // phase-tree-lock-open-shackle — the card no longer dims
                      // the unlocked state to 0.45 (a weight difference that read
                      // as absence, the same defect as the tree). Both states are
                      // the same solid colour; the SHAPE carries the meaning.
                      color: 'var(--text)',
                    }}
                  >
                    {/* phase-tree-lock-open-shackle — the SAME lock-state glyph
                        the CTD tree uses (converging the forked emoji pattern
                        onto one component). Decorative here: the button already
                        carries the accessible name and the toggle action. */}
                    <LockStateIcon locked={isLocked} size={15} decorative />
                  </button>
                </div>
                <div style={metaStyle}>
                  {doc.Classification ?? '—'}
                  {' · '}
                  {formatBytes(doc.FileSize)}
                  {' · '}
                  {operation}
                </div>
                {region === 'FDA' && sectionKey(doc.CtdSectionCode ?? doc.Classification ?? sectionCode) === '1.1' && (
                  <label style={{ display: 'block', marginTop: 6, fontSize: 12 }} onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
                    FDA form type
                    <select
                      aria-label={`FDA form type for ${doc.FileName}`}
                      data-testid={`fda-form-type-${doc.Id}`}
                      value={doc.Id in formTypes ? formTypes[doc.Id] ?? '' : doc.FdaFormType ?? ''}
                      disabled={!canEdit || sequenceLocked || isLocked || formTypeBusy === doc.Id}
                      style={{ display: 'block', width: '100%', minWidth: 0, marginTop: 4 }}
                      onChange={(event) => {
                        if (!canEdit || sequenceLocked || isLocked) return;
                        const FdaFormType = event.target.value || null;
                        setFormTypeBusy(doc.Id);
                        setFormTypeError(null);
                        void v2.documents.update(doc.Id, { FdaFormType }, doc.RowVersion).then(() => {
                          setFormTypes((previous) => ({ ...previous, [doc.Id]: FdaFormType }));
                          onListChanged?.();
                        }).catch((err: unknown) => {
                          setFormTypeError({ id: doc.Id, message: describeApiError(err, 'Could not save the FDA form type.').message });
                        }).finally(() => setFormTypeBusy(null));
                      }}
                    >
                      <option value="">Select the completed FDA form…</option>
                      {FDA_FORM_TYPES.map((form) => <option key={form.code} value={form.code}>{form.label}</option>)}
                    </select>
                    <span style={{ color: 'var(--text-muted)' }}>Identify the uploaded form before compiling. This does not fill or sign the form.</span>
                    {formTypeError?.id === doc.Id && <span role="alert" style={{ display: 'block', color: 'var(--red)' }}>{formTypeError.message}</span>}
                  </label>
                )}
                {/* Phase hc-dual-format-note (TODO-063) — HC dual-format note. */}
                {requiresDualFormat(doc, region) && (() => {
                  const hasPdf  = sectionHasPdf(doc, displayDocs);
                  const hasDocx = sectionHasDocx(doc, displayDocs);
                  if (hasPdf && hasDocx) {
                    return (
                      <div
                        data-testid={`hc-dual-format-ok-${doc.Id}`}
                        style={{ fontSize: 10, color: '#15803d', marginTop: 2 }}
                      >
                        ✓ Both formats present
                      </div>
                    );
                  }
                  if (!hasPdf) {
                    return (
                      <div
                        data-testid={`hc-dual-format-warn-pdf-${doc.Id}`}
                        style={{ fontSize: 10, color: '#92400e', marginTop: 2 }}
                      >
                        ⚠ HC requires PDF version
                      </div>
                    );
                  }
                  // !hasDocx
                  return (
                    <div
                      data-testid={`hc-dual-format-warn-docx-${doc.Id}`}
                      style={{ fontSize: 10, color: '#92400e', marginTop: 2 }}
                    >
                      ⚠ HC requires DOCX version
                    </div>
                  );
                })()}
                {/* Phase compiler-spl-review-badge (TODO-084) — SPL/XSL pairing note. */}
                {isSplXmlDoc(doc) ? (
                  sectionHasXsl(doc, displayDocs) ? (
                    <div
                      data-testid={`spl-xsl-paired-${doc.Id}`}
                      style={{ fontSize: 10, color: '#15803d', marginTop: 2 }}
                    >
                      ✓ XSL paired
                    </div>
                  ) : (
                    <div
                      data-testid={`spl-xsl-missing-${doc.Id}`}
                      style={{ fontSize: 10, color: '#92400e', marginTop: 2 }}
                    >
                      ⚠ SPL document — must be paired with its XSL stylesheet in the same section
                    </div>
                  )
                ) : null}
                {/* Phase UI-13 — ModifiedFile + inline rename row. */}
                {renameId === doc.Id ? (
                  <div style={{ display: 'flex', gap: 4, marginTop: 4 }} onClick={(e) => e.stopPropagation()}>
                    <input
                      data-testid={`rename-input-${doc.Id}`}
                      type="text"
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      disabled={renameBusy}
                      style={{
                        flex: 1,
                        padding: '3px 6px',
                        fontSize: 11,
                        border: '1px solid var(--border)',
                        borderRadius: 3,
                      }}
                    />
                    <button
                      type="button"
                      data-testid={`rename-save-${doc.Id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        void saveRename(doc.Id);
                      }}
                      disabled={renameBusy}
                      style={{ fontSize: 11, padding: '2px 8px' }}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      data-testid={`rename-cancel-${doc.Id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setRenameId(null);
                      }}
                      disabled={renameBusy}
                      style={{ fontSize: 11, padding: '2px 8px' }}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <>
                    {doc.ModifiedFile && doc.ModifiedFile !== doc.FileName ? (
                      <div
                        data-testid={`document-list-modified-${doc.Id}`}
                        style={{ ...metaStyle, fontStyle: 'italic' }}
                      >
                        Prior document: {doc.ModifiedFile as string}
                      </div>
                    ) : null}
                    {canEdit ? (
                      <div style={{ display: 'flex', gap: 4, marginTop: 2 }}>
                        <button
                          type="button"
                          data-testid={`rename-btn-${doc.Id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            startRename(doc);
                          }}
                          disabled={isLocked}
                          style={{
                            fontSize: 10,
                            padding: '1px 6px',
                            border: '1px solid var(--border)',
                            borderRadius: 3,
                            background: 'var(--surface)',
                            cursor: isLocked ? 'not-allowed' : 'pointer',
                            opacity: isLocked ? 0.35 : 1,
                          }}
                        >
                          Rename
                        </button>
                        <button
                          type="button"
                          data-testid={`auto-rename-btn-${doc.Id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            void autoRename(doc.Id);
                          }}
                          disabled={isLocked}
                          style={{
                            fontSize: 10,
                            padding: '1px 6px',
                            border: '1px solid var(--border)',
                            borderRadius: 3,
                            background: 'var(--surface)',
                            cursor: isLocked ? 'not-allowed' : 'pointer',
                            opacity: isLocked ? 0.35 : 1,
                          }}
                        >
                          Auto-rename
                        </button>
                        {/* phase-document-reorder-within-section — move up/down. */}
                        <button
                          type="button"
                          data-testid={`move-up-btn-${doc.Id}`}
                          title={isLocked ? 'Locked — unlock to move' : 'Move up'}
                          disabled={docIdx === 0 || isLocked}
                          onClick={(e) => {
                            e.stopPropagation();
                            void commitReorder(swapDocs(doc.Id, 'up'));
                          }}
                          style={{
                            fontSize: 10,
                            padding: '1px 4px',
                            border: '1px solid var(--border)',
                            borderRadius: 3,
                            background: 'var(--surface)',
                            cursor: docIdx === 0 || isLocked ? 'default' : 'pointer',
                            opacity: docIdx === 0 || isLocked ? 0.35 : 1,
                          }}
                        >
                          ▲
                        </button>
                        <button
                          type="button"
                          data-testid={`move-down-btn-${doc.Id}`}
                          title={isLocked ? 'Locked — unlock to move' : 'Move down'}
                          disabled={docIdx === displayDocs.length - 1 || isLocked}
                          onClick={(e) => {
                            e.stopPropagation();
                            void commitReorder(swapDocs(doc.Id, 'down'));
                          }}
                          style={{
                            fontSize: 10,
                            padding: '1px 4px',
                            border: '1px solid var(--border)',
                            borderRadius: 3,
                            background: 'var(--surface)',
                            cursor:
                              docIdx === displayDocs.length - 1 || isLocked
                                ? 'default'
                                : 'pointer',
                            opacity:
                              docIdx === displayDocs.length - 1 || isLocked ? 0.35 : 1,
                          }}
                        >
                          ▼
                        </button>
                        {/* Phase UI-13 — internal drag handle so rows can be reordered.
                            Phase compiler-document-lock — disabled when locked. */}
                        <span
                          data-testid={`drag-handle-${doc.Id}`}
                          draggable={!isLocked}
                          onDragStart={(e) => {
                            if (isLocked) {
                              e.preventDefault();
                              return;
                            }
                            e.dataTransfer.setData('application/x-document-id', doc.Id);
                            e.dataTransfer.effectAllowed = 'move';
                          }}
                          title={isLocked ? 'Locked — unlock to reorder' : 'Drag to reorder'}
                          aria-disabled={isLocked}
                          style={{
                            cursor: isLocked ? 'not-allowed' : 'grab',
                            padding: '0 4px',
                            color: 'var(--text-muted)',
                            userSelect: 'none',
                            opacity: isLocked ? 0.35 : 1,
                          }}
                        >
                          ⠿
                        </span>
                      </div>
                    ) : null}
                  </>
                )}
              </div>
            );
          })}
          </div>
        </div>
      )}

      {/* Phase sibling-selector-toolbar (TODO-058 Part B) — overlay of
          prior-sequence documents. Muted, read-only, badge labeled with
          the source sequence so publishers can see what existed in the
          prior submission alongside the active one. */}
      {siblingDocuments && siblingDocuments.length > 0 ? (
        <div data-testid="sibling-documents-section" style={{ marginTop: 16 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              color: 'var(--text-muted)',
              marginBottom: 6,
            }}
          >
            From prior sequence
          </div>
          <div role="list" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {siblingDocuments.map((doc) => (
              <div
                key={doc.Id}
                role="listitem"
                data-testid={`sibling-document-row-${doc.Id}`}
                style={{
                  ...rowBaseStyle,
                  cursor: 'default',
                  opacity: 0.6,
                  background: 'var(--surface-muted, rgba(148,163,184,0.06))',
                }}
              >
                <div style={fileNameStyle}>
                  <span data-testid={`sibling-document-filename-${doc.Id}`}>
                    {doc.FileName}
                  </span>
                  {siblingSequenceNumber ? (
                    <span
                      data-testid={`sibling-document-badge-${doc.Id}`}
                      style={{
                        marginLeft: 8,
                        padding: '1px 6px',
                        borderRadius: 4,
                        background: 'var(--surface2)',
                        border: '1px solid var(--border)',
                        fontSize: 10,
                        fontWeight: 600,
                        color: 'var(--text-muted)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                      }}
                    >
                      from seq {siblingSequenceNumber}
                    </span>
                  ) : null}
                </div>
                <div style={metaStyle}>
                  {doc.Classification ?? '—'}
                  {' · '}
                  {formatBytes(doc.FileSize)}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {pending ? (
        <ConfirmModal
          isOpen={true}
          title={`Revise ${pending.targetDoc.FileName}?`}
          message={`The new file "${pending.newFileName}" will revise the draft document${
            pending.targetDoc.CtdSectionCode
              ? ` at section ${pending.targetDoc.CtdSectionCode}`
              : ''
          }. Its lifecycle operation and prior-sequence target will stay the same. The old draft remains in the audit history but is marked superseded.`}
          confirmLabel={replacing ? 'Revising…' : 'Revise draft'}
          cancelLabel="Cancel"
          variant="destructive"
          isLoading={replacing}
          onConfirm={() => {
            void handleConfirmReplace();
          }}
          onCancel={handleCancelReplace}
        />
      ) : null}
    </div>
  );
}
